import Vue from "vue";
import {
  FeatureFlags,
  ProductStatus,
  Product,
  LoadingStatus
} from "../../lib/utilities/enums";

function initialState() {
  return {
    ViewAccountProducts: [],
    UserAccountProducts: [],
    FeatureLoadingStatus: LoadingStatus.Loading,
    DealersPackages: []
  };
}

const getActiveFeatures = accountProducts => {
  if (accountProducts && accountProducts.length > 0) {
    let liveProducts = accountProducts.filter(
      vap => vap.ProductStatusID == ProductStatus.Live
    );
    let activeFeatures = [];
    liveProducts.forEach(lp => {
      const filteredFeatures = lp.Features
        ? lp.Features.filter(ft => ft.Active)
        : [];
      if (filteredFeatures.length > 0)
        activeFeatures = activeFeatures.concat(filteredFeatures);
    });

    return activeFeatures;
  }
  return [];
};

// Filter and return stores that have the requested product (and feature) enabled
const getStores = (productFeatureList, storeList, [productID, featureID]) =>
  productFeatureList
    .filter(
      vap =>
        vap &&
        vap.ProductID == productID &&
        (!featureID || (vap.Features || []).find(feat => feat && feat.Active && feat.FeatureID == featureID))
    )
    .map(vap => storeList.find(vss => vss && vss.AccountPlatformID == vap.AccountPlatformID))
    .filter(vss => vss)
    .sort((a, b) => {
      return a.Name && b.Name && a.Name.toLowerCase() > b.Name.toLowerCase() ? 1 : -1;
    });

const hasFeatureForSelectedStores = (features, storeIds) => {
  return (features || []).some(feature =>
    storeIds.includes(feature.AccountPlatformID)
  );
};

const state = initialState();

const getters = {
  DealersPackages: state => platformId => {
    if (state.DealersPackages && state.DealersPackages.length > 0) {
      let requestedDealerSettings = state.DealersPackages.find(ds => {
        return ds.AccountPlatformID == platformId;
      });
      if (requestedDealerSettings) {
        return requestedDealerSettings;
      }
    }
  },
  ViewAccountProducts: function (state) {
    return state.ViewAccountProducts;
  },
  UserAccountProducts: function (state) {
    return state.UserAccountProducts;
  },
  ExperienceManagerStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, FeatureFlags.ExperienceManager]
    );
  },
  OnlineOfferStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.OneToOne, FeatureFlags.OnlineOffer]
    );
  },
  ILMStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, FeatureFlags.ILM]
    );
  },
  PALStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.PAL, FeatureFlags.PAL]
    );
  },
  VideoStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, FeatureFlags.VideoMessaging]
    );
  },
  AutoAssistantSalesStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, FeatureFlags.AutoAssistantSales]
    );
  },
  AutoAssistantServiceStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, FeatureFlags.AutoAssistantService]
    );
  },
  AutoAssistantLeadsStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.AutoAssistant, FeatureFlags.AutoAssistantLeads]
    );
  },
  CXMStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, FeatureFlags.CXM]
    );
  },
  SurveyStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.ReputationPro, FeatureFlags.Survey]
    );
  },
  CallTrackingStores: function (state, getters, rootState, rootGetters) {
    return getStores(
      getters.ViewAccountProducts,
      rootGetters.User.ViewSelectedStores,
      [Product.Pando, FeatureFlags.CallTracking]
    );
  },
  ViewActiveFeatures(state) {
    return getActiveFeatures(state.ViewAccountProducts);
  },
  ViewActiveFeatureIds(state, getters) {
    return getters.ViewActiveFeatures.map(feature => feature.FeatureID);
  },
  ViewActiveFeatureFlags(state, getters, rootState, rootGetters) {
    const selectedStoreIds = rootGetters.User.ViewSelectedStores.map(
      str => str.AccountPlatformID
    );

    const activeFeatureFlags = {
      AlertMinerFull: getters.ViewActiveFeatureIds.includes(
        FeatureFlags.AlertMinerFull
      ),
      CXM: getters.ViewActiveFeatureIds.includes(FeatureFlags.CXM),
      ILM: getters.ViewActiveFeatureIds.includes(FeatureFlags.ILM),
      Messages: getters.ViewActiveFeatureIds.includes(FeatureFlags.Messages),
      SelectedStores: {
        CXM: hasFeatureForSelectedStores(getters.CXMStores, selectedStoreIds),
        ILM: hasFeatureForSelectedStores(getters.ILMStores, selectedStoreIds)
      }
    };
    return activeFeatureFlags;
  },
  ViewActiveFeatureStores(state, getters, rootState, rootGetters) {
    return {
      CXMStoreIds: getters.CXMStores.map(str => str.AccountPlatformID),
      ILMStoreIds: getters.ILMStores.map(str => str.AccountPlatformID)
    };
  }
};

const actions = {
  getDealersPackages: function (context, platformId) {
    let request = {
      AccountPlatformId: platformId,
      PackageId: null
    };
    return new Promise((resolve, reject) => {
      Vue.http.post(`/packages/GetDealerPackage`, request).then(response => {
        if (response && response.data && response.data.Data) {
          context.commit("updateDealersPackages", response.data.Data);
          resolve(response.data.Data);
        }
      });
    });
  },
  checkViewAccountFeatures(context) {
    let requestPackage = {};
    requestPackage.AccountID = context.getters.User.ViewAccountID;
    requestPackage.PlatformIds = context.getters.User.ViewSelectedStores.map(
      m => m.AccountPlatformID
    );
    requestPackage.ProductIds = [
      Product.Pando,
      Product.OneToOne,
      Product.AutoAssistant,
      Product.ReputationPro,
      Product.PAL
    ];
    requestPackage.ProductStatus = [ProductStatus.Live];
    requestPackage.FeatureStatus = true;
    return context
      .dispatch("checkAccountFeatures", requestPackage)
      .then(function (data) {
        if (data && data.length > 0) {
          context.commit("updateViewAccountProducts", data);
        }
      });
  },
  checkUserAccountFeatures(context) {
    let requestPackage = {};
    requestPackage.AccountID = context.getters.User.AccountID;
    requestPackage.PlatformIds = context.getters.User.AllowedStores.map(
      m => m.AccountPlatformID
    );
    requestPackage.ProductIds = [Product.Pando];
    requestPackage.ProductStatus = [ProductStatus.Live];
    requestPackage.FeatureStatus = true;
    return context
      .dispatch("checkAccountFeatures", requestPackage)
      .then(function (data) {
        if (data && data.length > 0) {
          context.commit("updateUserAccountProducts", data);
        }
      });
  },
  checkAccountFeatures(context, requestPackage) {
    requestPackage.Size =
      requestPackage.PlatformIds.length * requestPackage.ProductIds.length;
    requestPackage.From = 0;
    return Vue.http.post("/products/", requestPackage).then(data => {
      if (data && data.data && data.data.Data.length > 0) {
        return data.data.Data;
      }
    });
  },
  hasRequestedAccountFeature(context, productFeature) {
    if (productFeature == FeatureFlags.OnlineOffer) {
      return OnlineOfferStores.length > 0;
    }
    if (productFeature == FeatureFlags.ILM) {
      return ILMStores.length > 0;
    }
    if (productFeature == FeatureFlags.ExperienceManager) {
      return ExperienceManagerStores.length > 0;
    }
    if (productFeature == FeatureFlags.CXM) {
      return CXMStores.length > 0;
    }
  }
};

const mutations = {
  updateViewAccountProducts: function (state, data) {
    state.ViewAccountProducts = data;
  },
  updateUserAccountProducts: function (state, data) {
    state.UserAccountProducts = data;
  },
  featureDetailsLoaded: function (state) {
    state.FeatureLoadingStatus = LoadingStatus.Loaded;
  },
  updateDealersPackages: function (state, data) {
    state.DealersPackages.push(data);
  }
};

export default {
  state,
  getters,
  actions,
  mutations
};



// WEBPACK FOOTER //
// ./src/store/modules/ProductFeatures.js